// The diagram converter, card.diagram (contract §8.4, Q26): the server's
// layout of a diagram (resolveNeoDiagramCard's snapshot `layout`, laid out
// once when the page is made) as one svg in the layout's own units, drawn as
// NeoDiagramDrawing (cloud/web/src/components/diagram) draws it: arrows under
// the boxes, each box tinted by its role, its label's lines and its reading
// lines, then each arrow's label box over both. A reading line is the
// reading's short words as fixed text, then its value and unit bound. From
// then on the page is the drawing: a later read never lays it out again.
//
// The one deliberate difference from today's card (Q25 over Q7): a reading's
// text is max(12, 14 × width / 360) units, so a value is at least 14 px on
// the medium card. Box and arrow labels are the model's words from the card,
// which ADR 0386's label rule already checked. The legend of the roles drawn
// stays under the drawing; the line saying when the readings were read does
// not, since each value is live. At night a box is outlined in its role's
// tint, not filled with it: a value on a tinted fill under the night tint
// reads below the app's own values (§9.6's night rule, TOM-665).

import { css, esc, h, n, output, readingNames, reading } from "./kit.js";

/** NEO_DIAGRAM_TEXT (@neomaris/protocol neo-diagram.ts). */
const T = {
  label: 13,
  reading: 12,
  edgeLabel: 11,
  lineHeight: 1.25,
  padX: 8,
  padY: 6,
  edgePadX: 4,
  edgePadY: 2,
};
const ROLES = [
  "source",
  "converter",
  "storage",
  "bus",
  "load",
  "device",
  "other",
];
const ROLE_NAMES = {
  source: "Source",
  converter: "Converter",
  storage: "Storage",
  bus: "Bus",
  load: "Load",
  device: "Device",
  other: "Other",
};
const ELLIPSIS = "…";

// A character's width in ems, by class: the table the server's layout sized
// every box with (cloud/src/core/neo/diagram-layout.ts), without the 8 % it
// adds for safety. A flat average per character cut names that fit their box
// as the app draws them ("Victron MultiP…", TOM-665).
const NARROW = new Set([..."ijl'|!.,:;`I"]);
const SLIM = new Set([...'frt()[]{}/\\-"*']);
const WIDE_LOWER = new Set([..."mw"]);
const WIDE_UPPER = new Set([..."MW%@"]);
function charEm(ch) {
  if (ch === " ") return 0.28;
  if (NARROW.has(ch)) return 0.24;
  if (SLIM.has(ch)) return 0.34;
  if (WIDE_LOWER.has(ch)) return 0.8;
  if (WIDE_UPPER.has(ch)) return 0.9;
  if (ch >= "0" && ch <= "9") return 0.56;
  if (ch >= "a" && ch <= "z") return 0.54;
  if (ch >= "A" && ch <= "Z") return 0.69;
  const code = ch.codePointAt(0) ?? 0;
  if (code < 0x80) return 0.6;
  if (
    (code >= 0x2e80 && code <= 0xa4cf) ||
    (code >= 0xff00 && code <= 0xffef) ||
    code >= 0x1f000
  )
    return 1;
  return 0.62;
}

/** A line's estimated width at `size`, a bold one 5 % wider. */
const measure = (text, size, bold) => {
  let em = 0;
  for (const ch of text) em += charEm(ch);
  return em * size * (bold ? 1.05 : 1);
};

/** `text` cut short with an ellipsis to fit `max` (the web's clipText), or null. */
function clip(text, max, size, bold) {
  if (measure(text, size, bold) <= max) return text;
  const chars = [...text];
  let lo = 0;
  let hi = chars.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (
      measure(
        `${chars.slice(0, mid).join("").trimEnd()}${ELLIPSIS}`,
        size,
        bold,
      ) <= max
    )
      lo = mid;
    else hi = mid - 1;
  }
  return lo === 0
    ? null
    : `${chars.slice(0, lo).join("").trimEnd()}${ELLIPSIS}`;
}

/** The arrow's line through its points, each bend rounded (the web's diagramEdgePath). */
function edgePath(points) {
  const p = (pt) => `${n(pt[0])} ${n(pt[1])}`;
  const [first, ...rest] = points;
  if (first === undefined) return "";
  if (rest.length <= 1)
    return `M${p(first)}${rest.length === 1 ? ` L${p(rest[0])}` : ""}`;
  let d = `M${p(first)}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const at = points[i];
    const next = points[i + 1];
    d += ` Q${p(at)} ${n((at[0] + next[0]) / 2)} ${n((at[1] + next[1]) / 2)}`;
  }
  return `${d} L${p(points[points.length - 1])}`;
}

/** Lines stacked and centred in a box, each centred across it (the web's stack). */
function stack(box, rows) {
  const heights = rows.map((r) => r.size * T.lineHeight);
  const total = heights.reduce((a, b) => a + b, 0);
  let top = box.y + (box.h - total) / 2;
  const x = box.x + box.w / 2;
  return rows.map((row, i) => {
    const y = top + heights[i] / 2;
    top += heights[i];
    return { ...row, x, y };
  });
}

function line(row) {
  const attrs = {
    class: `ln ln-${row.kind}`,
    x: n(row.x),
    y: n(row.y),
    "font-size": n(row.size),
  };
  if (row.kind !== "reading") return h("text", attrs, esc(row.words));
  return h(
    "text",
    attrs,
    row.words === null ? "" : [h("tspan", { class: "w" }, esc(row.words)), " "],
    h("tspan", { class: "v", "data-tc-text": row.name }),
    h("tspan", {
      class: "u",
      dx: n(row.size * 0.25),
      "data-tc-unit": row.name,
    }),
  );
}

/**
 * Each role's tint (cloud ADR 0396, TOM-678): a kind's colour from the
 * frame's tokens, mixed to a wash, where the drawing had its own 17 colours.
 * A source is power's, a converter water's, storage the engine's amber (the
 * drawing's orange before), a load navigation's and a device wind's; a bus
 * and any other box are grey. At
 * night every role is the night red, as before.
 */
const ROLE_TINT = {
  source: "var(--tc-power-fill)",
  converter: "var(--tc-water-fill)",
  storage: "var(--tc-engine-fill)",
  bus: "var(--quiet)",
  load: "var(--tc-navigation-fill)",
  device: "var(--tc-wind-fill)",
  other: "var(--quiet)",
};
const mix = (colour, percent) =>
  `color-mix(in srgb, ${colour} ${percent}%, transparent)`;
const tints = (percents) =>
  ROLES.map((r, i) => `--_${r}: ${mix(ROLE_TINT[r], percents[i])};`).join(" ");

const CSS = css(`
  .dg-w { gap: 10px; }
  .dg { display: block; width: 100%; height: auto; max-height: 640px; margin: 0 auto; overflow: visible;
    font-family: var(--tc-font-sans); }
  .dg, .dg-k { --_q: var(--muted); --_o: ${mix("var(--quiet)", 40)};
    ${tints([18, 16, 18, 22, 14, 16, 10])} }
  html[data-theme=dark] .dg, html[data-theme=dark] .dg-k { --_o: ${mix("var(--quiet)", 55)};
    ${tints([26, 28, 26, 36, 24, 28, 18])} }
  html[data-theme=night] .dg, html[data-theme=night] .dg-k { --_o: var(--grid);
    ${ROLES.map((r, i) => `--_${r}: ${mix("var(--tc-series-1)", [22, 10, 32, 40, 6, 15, 3][i])};`).join(" ")}
    ${ROLES.map((r, i) => `--_${r}n: ${mix("var(--tc-series-1)", [70, 45, 90, 100, 30, 55, 20][i])};`).join(" ")} }
  .dg .ln { fill: var(--text); text-anchor: middle; dominant-baseline: central; font-variant-numeric: tabular-nums; }
  .dg .ln-label { font-weight: 600; }
  .dg .ln-edge-label { fill: var(--muted); }
  .dg .w { fill: var(--_q); }
  .dg .v { font-weight: 600; }
  .dg .v[data-quality=stale], .dg .v[data-quality=missing] { fill: var(--_q); }
  /* At night a stale value keeps the text colour: grey under the night tint reads below the app's own values (§9.6, TOM-665). */
  html[data-theme=night] .dg .v[data-quality=stale] { fill: var(--text); }
  .dg .e { fill: none; stroke: var(--muted); stroke-width: 1.5; stroke-linejoin: round; }
  .dg .hd { fill: var(--muted); }
  .dg .el rect, .dg .nb { fill: var(--background); }
  .dg .nf { stroke: var(--_o); stroke-width: 1.25; }
  ${ROLES.map((r) => `.dg .r-${r} .nf { fill: var(--_${r}); }`).join(" ")}
  html[data-theme=night] .dg .nf { fill: var(--background); stroke-width: 1.5; }
  ${ROLES.map((r) => `html[data-theme=night] .dg .r-${r} .nf { stroke: var(--_${r}n); }`).join(" ")}
  .dg .r-other .nf { stroke-dasharray: 4 3; }
  .dg-k { display: flex; flex-wrap: wrap; gap: 4px 12px; margin: 0; padding: 0; list-style: none;
    color: var(--muted); font: 400 12px/1.3 var(--tc-font-sans); }
  .dg-k li { display: inline-flex; align-items: center; gap: 5px; }
  .dg-k i { flex: none; width: 12px; height: 12px; border: 1.25px solid var(--_o); border-radius: 3px; }
  ${ROLES.map((r) => `.dg-k .k-${r} i { background: var(--_${r}); }`).join(" ")}
  .dg-k .k-other i { border-style: dashed; }
`);

/** card.diagram: the diagram converter (§8.4). */
export function cardDiagram(source, context) {
  const spec = source.spec;
  const layout = context?.diagram?.layout;
  if (!layout || !Array.isArray(layout.nodes) || !Array.isArray(layout.edges))
    throw new Error("A diagram's template needs its layout");
  const nodesSpec = Array.isArray(spec.nodes) ? spec.nodes : [];
  const edgesSpec = Array.isArray(spec.edges) ? spec.edges : [];
  // Every path the diagram names, each once: boxes, then arrows (neoDiagramPaths).
  const paths = [];
  for (const node of nodesSpec)
    for (const r of node.readings ?? [])
      if (!paths.includes(r.path)) paths.push(r.path);
  for (const edge of edgesSpec)
    if (edge.reading && !paths.includes(edge.reading.path))
      paths.push(edge.reading.path);
  const byPath = readingNames(paths.slice(0, 12));
  const facts = new Map(
    (context.diagram.readings ?? []).map((r) => [r.path, r]),
  );
  const readings = {};
  for (const [path, name] of byPath) readings[name] = reading(path);
  // Q25: a value at least 14 px on the medium card (360 px wide).
  const size = Math.max(T.reading, (14 * layout.width) / 360);
  // The room a value takes beside its words: the layout's own four ems of the
  // reading's size (diagram-layout.ts's VALUE_EM).
  const valueRoom = 4 * size;

  const readingRow = (path, max) => {
    const name = byPath.get(path);
    if (name === undefined) return null;
    const short = facts.get(path)?.short ?? null;
    const room = max - valueRoom;
    return {
      kind: "reading",
      size,
      name,
      words:
        short === null || room <= 0 ? null : clip(short, room, size, false),
    };
  };

  const nodes = layout.nodes.map((box, i) => {
    const node = nodesSpec[i] ?? {};
    // Half the box's padding each side, as the web's EDGE_ROOM.
    const max = box.w - T.padX;
    const rows = [
      ...(box.lines ?? []).map((text) => ({
        kind: "label",
        size: T.label,
        words: clip(text, max, T.label, true) ?? ELLIPSIS,
      })),
      ...(node.readings ?? [])
        .map((r) => readingRow(r.path, max))
        .filter((r) => r !== null),
    ];
    const role = ROLES.includes(node.role) ? node.role : "other";
    return h(
      "g",
      { class: `r-${role}` },
      h("rect", {
        class: "nb",
        x: n(box.x),
        y: n(box.y),
        width: n(box.w),
        height: n(box.h),
        rx: 8,
      }),
      h("rect", {
        class: "nf",
        x: n(box.x),
        y: n(box.y),
        width: n(box.w),
        height: n(box.h),
        rx: 8,
      }),
      stack(box, rows).map(line),
    );
  });

  const edges = [];
  const labels = [];
  layout.edges.forEach((laid, i) => {
    const edge = edgesSpec[i] ?? {};
    const arrow = edge.arrow ?? "forward";
    edges.push(
      h("path", {
        class: "e",
        d: edgePath(laid.points ?? []),
        "marker-end": arrow === "none" ? null : "url(#dg-hd)",
        "marker-start": arrow === "both" ? "url(#dg-hd)" : null,
      }),
    );
    const box = laid.labelAt;
    if (box === null || box === undefined) return;
    const max = box.w - T.edgePadX;
    const rows = [];
    if (typeof edge.label === "string")
      rows.push({
        kind: "edge-label",
        size: T.edgeLabel,
        words: clip(edge.label, max, T.edgeLabel, false) ?? ELLIPSIS,
      });
    if (edge.reading) {
      const row = readingRow(edge.reading.path, max);
      if (row !== null) rows.push(row);
    }
    if (rows.length === 0) return;
    labels.push(
      h(
        "g",
        { class: "el" },
        h("rect", {
          x: n(box.x),
          y: n(box.y),
          width: n(box.w),
          height: n(box.h),
          rx: 4,
        }),
        stack(box, rows).map(line),
      ),
    );
  });

  const used = new Set(
    nodesSpec.map((nd) => (ROLES.includes(nd.role) ? nd.role : "other")),
  );
  const roles = ROLES.filter((r) => used.has(r));
  const count = (k, one, many) => `${k} ${k === 1 ? one : many}`;
  // The card's header shows the title; the label leaves it out, since the
  // check reads every aria-* value as text (§9.4) and the title is the model's.
  const summary = `Diagram of ${count(nodesSpec.length, "part", "parts")} and ${count(edgesSpec.length, "connection", "connections")}.`;
  const svg = h(
    "svg",
    {
      class: "dg",
      // No larger than the board drew it, 1.6 times its layout (the web's
      // neoDiagramFit.ts), so the drawing and its legend fit the tallest card.
      style: `max-width:${n(layout.width * 1.6)}px`,
      viewBox: `0 0 ${n(layout.width)} ${n(layout.height)}`,
      role: "img",
      "aria-label": summary,
    },
    h(
      "defs",
      {},
      h(
        "marker",
        {
          id: "dg-hd",
          viewBox: "0 0 10 10",
          refX: "10",
          refY: "5",
          markerWidth: "7",
          markerHeight: "7",
          markerUnits: "userSpaceOnUse",
          orient: "auto-start-reverse",
        },
        h("path", { class: "hd", d: "M0 0 L10 5 L0 10 z" }),
      ),
    ),
    h("g", {}, edges),
    h("g", {}, nodes),
    h("g", {}, labels),
  );
  const legend =
    roles.length > 1
      ? h(
          "ul",
          { class: "dg-k", "aria-hidden": "true" },
          roles.map((r) =>
            h("li", { class: `k-${r}` }, h("i", {}), ROLE_NAMES[r]),
          ),
        )
      : "";
  return output("card.diagram", 1, {
    html: h("div", { class: "tp dg-w" }, svg, legend),
    css: CSS,
    readings,
    card: { kind: "chart", window: false, chip: null },
  });
}
